import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { formatQuantity, planReceipt } from '@kronos/domain'
import { repository } from '../../data'
import type { InventoryItem, Receipt } from '../../data/inventory'
import { PageHeader, Section } from '../primitives'
import { ItemThumb } from '../inventory/ItemArt'
import { errorText, trackingText } from '../inventory/shared'
import { CountStepper } from './CountStepper'
import { MakerSerials } from './MakerSerials'

/**
 * Step 2 — how many, optionally which, and a note. One screen and one button.
 *
 * The count and the scanned serials are reconciled by `planReceipt`, the same
 * function the server runs: scan more boxes than the number says and the
 * number rises to match, so nobody has to scroll back up to fix it.
 */
export function ReceiveDetails({
  item,
  onBack,
  onDone,
}: {
  item: InventoryItem
  onBack: () => void
  onDone: (receipt: Receipt & { readonly onShelf: number }) => void
}): JSX.Element {
  const queryClient = useQueryClient()
  const serialized = item.tracking === 'serialized'
  const [count, setCount] = useState<number>(serialized ? 1 : 0)
  const [serials, setSerials] = useState<readonly string[]>([])
  const [note, setNote] = useState('')

  const plan = planReceipt({ tracking: item.tracking, quantity: count, makerSerials: serials, note })
  const quantity = plan.ok ? plan.plan.quantity : count

  const take = useMutation({
    mutationFn: () => repository.receive({ itemId: item.id, quantity, makerSerials: serials, note }),
    onSuccess: (receipt) => {
      for (const key of [['inventory'], ['inventory-item'], ['receipts'], ['stock'], ['categories']]) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
      onDone(receipt)
    },
  })

  const label =
    quantity <= 0
      ? serialized
        ? 'Say how many arrived'
        : 'Say how much arrived'
      : serialized
        ? `Receive ${quantity} × ${item.name}`
        : `Receive ${formatQuantity(quantity, item.unit)} of ${item.name}`

  return (
    <div className="page page--narrow">
      <PageHeader
        eyebrow={item.category || undefined}
        title={
          <span className="inv-title">
            <ItemThumb imageId={item.imageId} category={item.category} name={item.name} size={52} />
            {item.name}
          </span>
        }
        subtitle={`${trackingText(item.tracking, item.unit)} · ${formatQuantity(item.onShelf, item.unit)} on the shelf now`}
        actions={
          <button type="button" onClick={onBack}>
            Change item
          </button>
        }
      />

      <Section label={serialized ? 'How many arrived?' : `How much arrived${item.unit !== 'each' ? `, in ${item.unit}` : ''}?`}>
        <CountStepper value={count} onChange={setCount} whole={serialized} min={serialized ? Math.max(1, serials.length) : 0} />
      </Section>

      {serialized ? (
        <Section label="Maker’s serial numbers · optional">
          <MakerSerials
            serials={serials}
            expected={quantity}
            onChange={(next) => {
              setSerials(next)
              // The 23rd box scanned says it was 23, not 22.
              setCount((c) => Math.max(c, next.length))
            }}
          />
          <p className="muted inv-quiet">
            {item.marking === 'qr_label'
              ? 'Kronos numbers every one now. Its label can be printed later.'
              : 'Kronos numbers every one now, whether or not you add these.'}
          </p>
        </Section>
      ) : null}

      <Section label="Note · optional">
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Supplier, PO or packing slip number"
          aria-label="Note"
          className="inv-note"
          maxLength={500}
        />
      </Section>

      {!plan.ok && count !== 0 ? <p className="critical">{plan.detail}</p> : null}
      {take.isError ? <p className="critical">{errorText(take.error)}</p> : null}

      <button
        type="button"
        className="primary inv-confirm"
        disabled={!plan.ok || take.isPending}
        onClick={() => take.mutate()}
      >
        {take.isPending ? 'Receiving…' : label}
      </button>
    </div>
  )
}
